import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';

import { setSelectedMember } from '@actions/common/actionCreators';
import {
    fetchConsumerInboxUnreadNotificationCount
} from '@actions/individualPortal/secureInbox/secureInboxActionCreators';
import { fetchPermissionDetails } from '@actions/memberPortal/enrollmentsActionCreators';
import {
    fetchSideNavUnreadNotificationCount,
    getStoredEncHouseholdId,
    normalizeUserRefId,
    resolveSideNavReferenceId,
    setSideNavdetails,
    syncSideNavReferenceId,
} from '@actions/SideNav/sideNavActionCreators';
import { USER_PROFILE } from '@constants/common/statusTypes';
import { CONFIDENTIAL_ROUTE_KEYS } from '@constants/members/members.constants';
import NavBarContainer from '@containers/common/NavBarContainer';
import { useAppDispatch, useAppSelector, useBroker } from '@hooks/index';
import useMemberNameHistory from '@hooks/useMemberNameHistory';
import useQueryParams from '@hooks/useQueryParams';
import { IRemoteProps } from '@modules/members/enrollments/common/types';
import { getSideNavDetails, getUnreadMessageCountForSideNav } from '@reducers/appReducer';
import { environmentSelector } from '@selectors/commonSelectors';
import { Layouts } from 'app-components';
import { useIntl } from 'react-intl';
import { ConditionalRendering } from '../conditional-rendering';
import Tooltip from '../tooltip/Tooltip';
import AdminMemberSideNav from './AdminMemberSideNav';

const LayoutContainer = styled.div`
  .grid-row {
    padding: 0;
  }

  .grid-col-12 {
    padding: 0;
  }

  #gi-sidebar {
    padding-left: 0;
    padding-right: 24px;
  }

  .confidential-user-info{
    color: ${({ theme }) => theme.dangerMain};
    display: flex;
    align-items:center;
    padding: 0 0 0 10px;
  }

`;

interface ISideNavProps {
  actions: {
    setSelectedMember: ( member: any ) => void;
  };
  activeModuleName: string;
  selectedMember: any;
  environment: any;
  children: React.ReactNode;
  page: string;
}

const SideNav = props => {
    const {environment, extProps, capTicketEnabled, numberUnread, page, updatePropsForAccUnlock} = props;
    return (
        <aside className="desktop:grid-col-3" id="gi-sidebar" data-testid="gi-sidebar">
            <AdminMemberSideNav
                environment={environment}
                extProps={extProps}
                capTicketEnabled={capTicketEnabled}
                unreadMessageCount={numberUnread}
                page={page}
                updateUnblockResponse={updatePropsForAccUnlock}
            />
        </aside>
    );
};

/** First truthy value, or '' when every candidate is empty/nullish. */
const firstNonEmpty = (...values: Array<string | null | undefined>) => values.find(Boolean) ?? '';

const SideNavContainer: React.FC<ISideNavProps> = ( {
    actions: {setSelectedMember},
    activeModuleName,
    selectedMember,
    environment,
    children,
    page,
} ) => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const intl = useIntl();
    const queryParams = useQueryParams();
    const applicationSessionData = {} as IRemoteProps;
    const [ updateProps, setUpdateProps ] = useState(false);
    const { capTicketEnabled } = useSelector(environmentSelector);
    const { id } = useParams();
    const hhIdFromQueryParams = queryParams.get('hhid');
    const referenceIdFromQuery = queryParams.get('referenceId');
    const sideNavDetails = useSelector(getSideNavDetails);
    const numberUnread = useSelector(getUnreadMessageCountForSideNav);
    const enrollmentDetails = useAppSelector(state => state.memberEnrollment?.permissions);
    const { isHouseIconVisible } = useBroker();
    const { nameHistory, fetchNameHistory } = useMemberNameHistory();

    // Authoritative sources, in priority order. Intentionally excludes sideNavDetails.encHHId —
    // that Redux value would make hasSideNavPermsForHousehold tautological after any prior
    // household visit on a parameterless route (premiumhistory/:eId, eligibility, etc.).
    const routeOrEnrollmentEncHouseholdId = firstNonEmpty(
        id,
        hhIdFromQueryParams,
        enrollmentDetails?.encHouseholdId,
        getStoredEncHouseholdId(),
    );

    // Display/link fallback: Redux encHHId only when no authoritative source exists.
    const encHouseholdId = firstNonEmpty(routeOrEnrollmentEncHouseholdId, sideNavDetails?.encHHId);

    const resolvedReferenceId = useMemo(
        () => resolveSideNavReferenceId(referenceIdFromQuery, enrollmentDetails?.household?.userRefId),
        [referenceIdFromQuery, enrollmentDetails?.household],
    );

    // Stable, deterministic member key (plaintext household id) — never the non-deterministic encHHId.
    const currentMemberKey = String(
        enrollmentDetails?.householdId ?? sideNavDetails?.hhId ?? hhIdFromQueryParams ?? '',
    );

    // True when Redux already has this household's perms. Compare against the authoritative
    // id only — never against encHouseholdId when it was derived from sideNavDetails.encHHId.
    // Redux resets on reload, so a reload/deep link refetches; in-app navigation does not.
    const hasSideNavPermsForHousehold =
        Boolean(routeOrEnrollmentEncHouseholdId) &&
        sideNavDetails?.encHHId === routeOrEnrollmentEncHouseholdId &&
        Object.keys(sideNavDetails?.permissionMap ?? {}).length > 0;

    useEffect(() => {
        if (selectedMember && id && selectedMember?.encHouseHoldId !== id) {
            setSelectedMember({...selectedMember, encHouseHoldId: id});
        }
    }, [ selectedMember, id, setSelectedMember ]);

    useEffect(() => {
        if (resolvedReferenceId) {
            dispatch(syncSideNavReferenceId(resolvedReferenceId));
        }
    }, [dispatch, resolvedReferenceId]);

    // Sole clear authority: drop the prior ref on a real member switch (plaintext id change,
    // no URL ref) to avoid a session-fallback leak. Plaintext id ignores encHHId churn.
    const previousMemberKeyRef = useRef<string>('');
    useEffect(() => {
        const previousMemberKey = previousMemberKeyRef.current;
        previousMemberKeyRef.current = currentMemberKey;
        const hasUrlRef = Boolean(normalizeUserRefId(referenceIdFromQuery));
        if (previousMemberKey && currentMemberKey && previousMemberKey !== currentMemberKey && !hasUrlRef) {
            dispatch(syncSideNavReferenceId(''));
        }
    }, [dispatch, currentMemberKey, referenceIdFromQuery]);

    useEffect(() => {
        const fetchData = async () => {
            const updateApplicationSessionData = () => {
                if (sessionStorage) {
                    Object.entries(sessionStorage).forEach(( item: [ string, any ] ) => {
                        applicationSessionData[item[0]] = item[1];
                    });
                }
                setSelectedMember(applicationSessionData);
            };

            const parsePermissionMap = ( permissionMap: any ) => {
                if (!permissionMap) return null;
                const t = JSON.stringify(permissionMap).replace(/[{}"]/g, '');
                const table = t.split(', ').map(pair => pair.split('='));
                return Object.fromEntries(table);
            };

            updateApplicationSessionData();
            const permissionMapRes = parsePermissionMap(applicationSessionData.permissionMap);
            dispatch(fetchSideNavUnreadNotificationCount('householdId', hhIdFromQueryParams || applicationSessionData.householdId));
            dispatch(
                setSideNavdetails({
                    idmUserStatus: applicationSessionData.idmUserStatus,
                    userStatus: applicationSessionData.userStatus,
                    hhUserId: applicationSessionData.householdUserId,
                    hhFirstName: applicationSessionData.householdFirstName,
                    hhLastName: applicationSessionData.householdLastName,
                    encHHId: applicationSessionData.encHouseHoldId || encHouseholdId || '',
                    encUserId: applicationSessionData.encryptedUserId,
                    hhId: applicationSessionData.householdId || '',
                    hhUser: applicationSessionData.householdUser,
                    calheersEnv: applicationSessionData.calheersEnv,
                    addNewViewCaseUrl: applicationSessionData.addNewViewCaseUrl,
                    checkDilog: applicationSessionData.checkDilog,
                    permissionMap: permissionMapRes,
                    switchToModuleName: applicationSessionData.switchToModuleName,
                    hhEmail: applicationSessionData.householdEmail,
                    hhPhone: applicationSessionData.householdPhone,
                    hhUserStatus: applicationSessionData.householdUserStatus,
                    activeModuleName,
                }),
            );
            dispatch(fetchConsumerInboxUnreadNotificationCount('householdId', encHouseholdId || id || ''));
        };
        localStorage.HHID = sideNavDetails.hhId;
        fetchData();
    }, [ updateProps, activeModuleName, setSelectedMember ]);

    useEffect(() => {
        // Gate on Redux (not sessionStorage): on a reload/deep link Redux is empty even if the
        // sessionStorage marker persists, so this correctly refetches.
        if (!encHouseholdId || hasSideNavPermsForHousehold) return;
        dispatch(fetchPermissionDetails(encHouseholdId));
    }, [dispatch, encHouseholdId, hasSideNavPermsForHousehold]);

    // Deep-link premiumhistory/:eId resolves household after enrollment details load — refetch inbox then.
    // Skip when route already has :id / ?hhid=; the mount effect above already covers that case.
    useEffect(() => {
        if (!encHouseholdId || id || hhIdFromQueryParams) return;
        dispatch(fetchConsumerInboxUnreadNotificationCount('householdId', encHouseholdId));
    }, [dispatch, encHouseholdId, id, hhIdFromQueryParams]);

    // Load the member's name-change history for the header hover (shown only when the member was
    // renamed). Fetched on member load so it is available to keyboard/focus users, matching the
    // side-nav data-on-load pattern; the read is a lightweight Envers audit lookup.
    useEffect(() => {
        const encHHId = id || hhIdFromQueryParams || encHouseholdId;
        if (encHHId) {
            fetchNameHistory(encHHId);
        }
    }, [ id, hhIdFromQueryParams, encHouseholdId, fetchNameHistory ]);

    useEffect(() => {
        if (!enrollmentDetails || enrollmentDetails?.permissionMap || hasSideNavPermsForHousehold) return;
        // Deep-link premiumhistory/:eId may seed a partial permissions object before menuPermissions
        // arrive — never call Object.entries on undefined (blank page / TypeError).
        if (!enrollmentDetails.menuPermissions || !enrollmentDetails.household) return;

        const permissionMap = Object.entries(enrollmentDetails.menuPermissions).reduce((acc, item) => {
            acc[item[0]] = item[1] ? 'true' : 'false';
            return acc;
        }, {});

        const lcSwitchModuleName = localStorage.getItem('lcSwitchModuleName');
        const hhEmail = localStorage.getItem('lcHhEmail');

        const sideNavDetailsPayload = {
            idmUserStatus: enrollmentDetails.idmUserStatus,
            userStatus: enrollmentDetails.userStatus,
            hhFirstName: enrollmentDetails.household.firstName,
            hhLastName: enrollmentDetails.household.lastName,
            encHHId: encHouseholdId || enrollmentDetails.encHouseholdId || '',
            hhId: enrollmentDetails.householdId || '',
            hhUser: enrollmentDetails.household,
            permissionMap,
            hhPhone: enrollmentDetails.household.phoneNumber,
            hhEmail,
            switchToModuleName: lcSwitchModuleName,
        };

        dispatch(setSideNavdetails(sideNavDetailsPayload));
    }, [enrollmentDetails, encHouseholdId, hasSideNavPermsForHousehold]);

    const updatePropsForAccUnlock = () => {
        sessionStorage.setItem('userStatus', USER_PROFILE.ACTIVE);
        sessionStorage.setItem('hhUserStatus', USER_PROFILE.ACTIVE);
        sessionStorage.setItem('idmUserStatus', USER_PROFILE.ENABLED);
        setUpdateProps(true);
    };

    const { hhFirstName = '', hhLastName = '', hhId = '', isConfidential = false } = sideNavDetails || {};

    const isConfidentialRoute = CONFIDENTIAL_ROUTE_KEYS.some(route => location.pathname.includes(route));

    // Current name stays in the header; on hover show the dated name-change trail. Built lazily
    // (only recomputed when the history changes) since the tooltip renders only for renamed members.
    const nameHistoryContent = useMemo(() => (
        <div data-testid="member-name-history-tooltip">
            {nameHistory.map((entry, index) => (
                <div key={`${index}-${entry.firstName}-${entry.lastName}`}>
                    {entry.effectiveDate ? `${entry.effectiveDate}: ` : ''}{entry.firstName} {entry.lastName}
                </div>
            ))}
        </div>
    ), [ nameHistory ]);

    return (
        <LayoutContainer>
            <Layouts id="sidenav-layout">
                <Layouts.Row hasGap>
                    <div className="grid-col-12">
                        <div className="grid-row">
                            <NavBarContainer
                                navBarClass="ticket-mgmt-top-nav grid-col-12"
                                houseIcon={isHouseIconVisible}
                            />
                        </div>
                        {(hhFirstName || hhLastName || hhId) && (
                            <div className="grid-row padding-t-20">
                                <h1 className="householdInfo" data-testid="householdInfo">
                                    <ConditionalRendering isTrue={nameHistory.length > 1}>
                                        <Tooltip
                                            id={<span>{hhFirstName} {hhLastName}</span>}
                                            index="memberNameHistory"
                                            elementId="member_name_history"
                                            content={nameHistoryContent}
                                            place="bottom"
                                            effect="solid"
                                            ariaLabel={intl.formatMessage({ id: 'sidenav.memberNameHistory.aria.label' })}
                                        />
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={nameHistory.length <= 1}>
                                        {hhFirstName} {hhLastName}
                                    </ConditionalRendering>
                                    {' '}(ID: {hhId})
                                </h1>
                                <ConditionalRendering isTrue={isConfidential && isConfidentialRoute}>
                                    <h3
                                        className="confidential-user-info"
                                        aria-label={intl.formatMessage({ id: 'sidenav.confidential.aria.label' })}
                                    >
                                        {intl.formatMessage({ id: 'sidenav.confidential.label' })}
                                    </h3>
                                </ConditionalRendering>
                            </div>
                        )}
                    </div>
                    <SideNav
                        {...{
                            environment,    
                            extProps: sideNavDetails,
                            capTicketEnabled,
                            numberUnread,
                            page,
                            updatePropsForAccUnlock,
                        }}
                    />
                    {children}
                </Layouts.Row>
            </Layouts>
        </LayoutContainer>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    activeModuleName: state.common.environment.user.activeModuleName,
    selectedMember: state.common?.sessionDataReducer?.selectedMember,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({setSelectedMember}, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(SideNavContainer);
